import { SubmitHandler } from "react-hook-form";
import { StudentFormData, useStudentForm } from "./StudentDataFormProvider";
import { ShortTextField } from "./Fields/ShortTextField";
import { Card, CircularProgress, Stack, styled, Typography } from "@mui/material";
import { LongTextField } from "./Fields/LongTextField";
import { FileUploadField } from "./Fields/FileUploadField";
import { CheckboxField } from "./Fields/CheckboxField";
import { DateField } from "./Fields/DateField";
import { createAdmissionForm } from "../Services/firebase";
import { useMutation } from "react-query";
import { PropsWithChildren, useRef, useState } from "react";
import { DropdownField } from "./Fields/DropdownField";

export const AdmissionForm = () => {
  const { handleSubmit } = useStudentForm();
  const [submissionError, setSubmissionError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const onSubmit: SubmitHandler<StudentFormData> = (data) => handleAdd(data);
  const { mutateAsync, data: formId } = useMutation(createAdmissionForm);
  const formSubmitted = useRef(false);

  const handleAdd = (data: StudentFormData) => {
    if (formSubmitted.current === true) return;
    setLoading(true);
    mutateAsync(data, {
      onSuccess: () => {
        formSubmitted.current = true;
        setSubmissionError(undefined);
      },
      onError: (e) => {
        console.log(e);
        formSubmitted.current = false;
        setSubmissionError("Something went wrong, please try again.");
      },
      onSettled: () => {
        setLoading(false);
      },
    });
  };

  return (
    <Card
      sx={{
        borderRadius: "1.5rem",
        borderTopLeftRadius: "0",
        marginTop: { xs: "1rem", md: "10rem" },
        width: { xs: "95%", md: "80%" },
        minHeight: "60vh",
        overflow: "visible",
        alignSelf: "center",
        position: "relative",
        zIndex: 2,
      }}
      elevation={2}
    >
      <FormHeader padding={{ xs: "0.5rem 1rem", md: "1rem 2rem" }} top={{ xs: "-2.8rem", md: "-3.5rem" }}>
        <Typography variant="h5" component={"p"} color={"#fff"}>
          <b>Admission Form</b>
        </Typography>
      </FormHeader>
      {loading || formSubmitted.current ? (
        <Stack
          gap={"1rem"}
          padding={{ xs: "1rem", md: "2rem" }}
          height={"100%"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          {loading ? <CircularProgress /> : `Your form has been submitted. Id: ${formId}`}
        </Stack>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={"1rem"} padding={{ xs: "1rem", md: "2rem" }}>
            <SubHeader dense>Student Details</SubHeader>
            <HorizontalStack direction={{ xs: "column", md: "row" }}>
              <ShortTextField fieldName="firstName" label="Student's First Name" required />
              <ShortTextField fieldName="lastName" label="Student's Last Name" required />
              <ShortTextField fieldName="nationality" label="Student's Nationality" required />
            </HorizontalStack>

            <HorizontalStack direction={{ xs: "column", md: "row" }}>
              <DropdownField
                fieldName="gender"
                label="Student's Gender"
                required
                options={[
                  { label: "Male", value: "Male" },
                  { label: "Female", value: "Female" },
                ]}
              />
              <DateField fieldName="dateOfBirth" label="Student's Date Of Birth" required />
              <ShortTextField
                fieldName="homeSpokenLanguage"
                label="Student's Home Spoken Language"
                required
              />
            </HorizontalStack>
            <LongTextField fieldName="address" label="Full Address" required />
            <LongTextField
              fieldName="specialRequirements"
              label="Does you child have any special requirements?"
              required
            />

            <HorizontalStack direction={{ xs: "column", md: "row" }}>
              <ShortTextField
                fieldName="lastSchoolAttended"
                label="Please enter the name of the school your child last attended"
              />

              <FileUploadField
                fieldName="leavingCertificate"
                label="Please upload your child's school leaving certificate"
                dependsOn="lastSchoolAttended"
              />
            </HorizontalStack>

            <SubHeader>Parent Details</SubHeader>
            <HorizontalStack direction={{ xs: "column", md: "row" }}>
              <ShortTextField fieldName="motherFullName" label="Mother's Full Name" required />
              <ShortTextField fieldName="motherContactNumber" label="Mother's Contract Number" required />
            </HorizontalStack>
            <HorizontalStack direction={{ xs: "column", md: "row" }}>
              <ShortTextField fieldName="motherEmail" label="Mother's Email" required />
              <ShortTextField fieldName="motherQualification" label="Mother's Qualification" required />
            </HorizontalStack>

            <HorizontalStack direction={{ xs: "column", md: "row" }}>
              <ShortTextField fieldName="fatherFullName" label="Father's Full Name" required />
              <ShortTextField fieldName="fatherContactNumber" label="Father's Contact Number" required />
            </HorizontalStack>
            <HorizontalStack direction={{ xs: "column", md: "row" }}>
              <ShortTextField fieldName="fatherEmail" label="Father's Email" required />
              <ShortTextField fieldName="fatherQualification" label="Father's Qualification" required />
            </HorizontalStack>

            <SubHeader>Emergency Contact Details</SubHeader>
            <HorizontalStack direction={{ xs: "column", md: "row" }}>
              <ShortTextField fieldName="emergencyContactName" label="Emergency Contact Full Name" required />
              <ShortTextField
                fieldName="emergencyContactNumber"
                label="Emergency Contact's Mobile Number"
                required
              />
            </HorizontalStack>

            <HorizontalStack direction={{ xs: "column", md: "row" }}>
              <ShortTextField fieldName="doctorFullName" label="Student's Doctor Full Name" required />
              <ShortTextField
                fieldName="doctorContactNumber"
                label="Student's Doctor Contact Number"
                required
              />
              <ShortTextField
                fieldName="doctorHospitalName"
                label="Student's Doctor Hospital Name"
                required
              />
            </HorizontalStack>

            <SubHeader>Document Section</SubHeader>
            <HorizontalStack direction={{ xs: "column", md: "row" }}>
              <FileUploadField
                fieldName="passportPicture"
                label="Please Upload Student's Passport Size Picture  (used for report card)"
                required
                fileType="image"
              />

              <FileUploadField
                fieldName="birthCertificate"
                label="Please Upload Student's Birth Certificate"
                required
              />
            </HorizontalStack>

            <HorizontalStack direction={{ xs: "column", md: "row" }}>
              <FileUploadField
                fieldName="passportFrontPage"
                label="Please Upload Student's Passport Front Page"
                required
              />

              <FileUploadField
                fieldName="passportLastPage"
                label="Please Upload Student's Passport Last Page"
                required
              />
            </HorizontalStack>

            <HorizontalStack direction={{ xs: "column", md: "row" }}>
              <FileUploadField fieldName="drcVisa" label="Please Upload Student's DRC Visa" required />
            </HorizontalStack>

            <SubHeader>Acknowledgement</SubHeader>
            <CheckboxField
              fieldName="acknowledgement"
              label="By selecting this checkbox I confirm that the information entered above is accurate as of the date of submission and I am happy for G.S. Little Genius School to store and use the above provided information."
              required
            />

            {submissionError !== undefined && (
              <Typography variant="caption" sx={{ color: "red" }}>
                {submissionError}
              </Typography>
            )}
            <SubmitButton type="submit" />
          </Stack>
        </form>
      )}
    </Card>
  );
};

const HorizontalStack = styled(Stack)({
  justifyContent: "space-between",
  alignContent: "center",
  gap: "2rem",
});

const SubmitButton = styled("input")({
  borderRadius: "10px",
  padding: "0.5rem",
  border: "none",
  background: "#4682B4",
  color: "#fff",
  "&:hover": {
    cursor: "pointer",
    background: "rgba*",
  },
});

const FormHeader = styled(Stack)({
  position: "absolute",
  left: 0,
  top: "-4rem",
  borderTopLeftRadius: "1.5rem",
  borderTopRightRadius: "2.5rem",
  borderBottomRightRadius: "0.5rem",
  padding: "1rem 2rem",
  background: "#F1594F",
  zIndex: 1,
});

const SubHeader = ({ children, dense = false }: PropsWithChildren<{ dense?: boolean }>) => {
  return (
    <Stack marginTop={dense ? "0" : "1rem"}>
      <Typography variant="h6" component={"p"} color={"#565656"}>
        {children}
      </Typography>
      <hr style={{ borderTop: "1px solid #adadadad" }} />
    </Stack>
  );
};
